// src/components/dev/RoleSwitcherSimple.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Chip,
  Typography,
  Card,
  CardContent,
  Fade,
  Alert,
  Grid,
  Avatar,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  SwapHoriz as SwapIcon,
  AdminPanelSettings as AdminIcon,
  Work as OrganizerIcon,
  Timer as TimerIcon,
  School as CoachIcon,
  DirectionsRun as AthleteIcon,
  Gavel as JudgeIcon,
  LiveTv as TransmissionIcon,
  Mic as CommentatorIcon,
  Article as PressIcon,
  CameraAlt as PhotographerIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';

const RoleSwitcherSimple = ({ currentRole, onRoleChange, showInProduction = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  // Solo mostrar en desarrollo a menos que se especifique lo contrario
  if (process.env.NODE_ENV === 'production' && !showInProduction) {
    return null;
  }

  const roles = [
    { 
      id: 'admin', 
      name: 'Administrador', 
      icon: <AdminIcon />, 
      color: 'error',
      description: 'Control total del sistema'
    },
    { 
      id: 'organizador', 
      name: 'Organizador', 
      icon: <OrganizerIcon />, 
      color: 'primary',
      description: 'Gestión de eventos'
    },
    { 
      id: 'cronometrador', 
      name: 'Cronometrador', 
      icon: <TimerIcon />, 
      color: 'secondary',
      description: 'Medición de tiempos'
    },
    { 
      id: 'entrenador', 
      name: 'Entrenador', 
      icon: <CoachIcon />, 
      color: 'info',
      description: 'Seguimiento de atletas'
    },
    { 
      id: 'atleta', 
      name: 'Atleta', 
      icon: <AthleteIcon />, 
      color: 'success',
      description: 'Progreso personal'
    },
    { 
      id: 'juez', 
      name: 'Juez', 
      icon: <JudgeIcon />, 
      color: 'warning',
      description: 'Arbitraje deportivo'
    },
    { 
      id: 'transmision', 
      name: 'Transmisión', 
      icon: <TransmissionIcon />, 
      color: 'error',
      description: 'Producción audiovisual'
    },
    { 
      id: 'locutor', 
      name: 'Locutor', 
      icon: <CommentatorIcon />, 
      color: 'primary',
      description: 'Comentarios deportivos'
    },
    { 
      id: 'prensa', 
      name: 'Prensa', 
      icon: <PressIcon />, 
      color: 'secondary',
      description: 'Cobertura periodística'
    },
    { 
      id: 'fotografo', 
      name: 'Fotógrafo', 
      icon: <PhotographerIcon />, 
      color: 'info',
      description: 'Documentación visual'
    }
  ];

  const currentRoleData = roles.find(role => role.id === currentRole) || roles[4]; // Default a atleta

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRoleSelect = (roleId) => {
    onRoleChange(roleId);
    handleClose();
    setShowAlert(true);
    
    // Ocultar alerta después de 3 segundos
    setTimeout(() => setShowAlert(false), 3000);
  };

  return (
    <Box>
      {/* Alerta de cambio */}
      <Fade in={showAlert}>
        <Box sx={{ position: 'fixed', top: 100, right: 24, zIndex: 9999 }}>
          <Alert 
            severity="success" 
            onClose={() => setShowAlert(false)}
            sx={{ minWidth: 300 }}
          >
            <Typography variant="body2">
              <strong>Rol cambiado a:</strong> {currentRoleData.name}
            </Typography>
          </Alert>
        </Box>
      </Fade>

      {/* Botón principal */}
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<SwapIcon />}
        endIcon={<ArrowDownIcon />}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.main'
          }
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {currentRoleData.icon}
          <Box textAlign="left">
            <Typography variant="body2" fontWeight="600">
              {currentRoleData.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Cambiar rol
            </Typography>
          </Box>
        </Box>
      </Button>

      {/* Menu de roles */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 320,
            mt: 1
          }
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" gutterBottom>
            🎭 Selector de Roles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Selecciona un rol para ver su dashboard específico
          </Typography>
        </Box>
        
        {roles.map((role) => (
          <MenuItem
            key={role.id}
            onClick={() => handleRoleSelect(role.id)}
            selected={role.id === currentRole}
            sx={{
              py: 1.5,
              px: 2,
              '&.Mui-selected': {
                bgcolor: `${role.color}.light`,
                '&:hover': {
                  bgcolor: `${role.color}.light`,
                }
              }
            }}
          >
            <ListItemIcon>
              <Avatar
                sx={{
                  bgcolor: `${role.color}.main`,
                  width: 32,
                  height: 32
                }}
              >
                {React.cloneElement(role.icon, { fontSize: 'small' })}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="600">
                    {role.name}
                  </Typography>
                  {role.id === currentRole && (
                    <Chip label="Actual" size="small" color={role.color} />
                  )}
                </Box>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {role.description}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default RoleSwitcherSimple;